import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const services = [
  {
    icon: "ri-heart-pulse-line",
    title: "Diabetes Management",
    description:
      "Comprehensive evaluation and management of Type 1 & Type 2 diabetes, including insulin therapy, CGM interpretation, and lifestyle counseling.",
    duration: "60 min",
    type: "In-Person / Telehealth",
  },
  {
    icon: "ri-shield-check-line",
    title: "Thyroid Disorders",
    description:
      "Diagnosis and treatment of hypothyroidism, hyperthyroidism, Hashimoto's, Graves' disease, thyroid nodules, and thyroid cancer follow-up.",
    duration: "45 min",
    type: "In-Person / Telehealth",
  },
  {
    icon: "ri-mental-health-line",
    title: "Hormonal Health",
    description:
      "Evaluation of adrenal disorders, pituitary conditions, PCOS, menopause, testosterone deficiency, and other hormonal imbalances.",
    duration: "60 min",
    type: "In-Person / Telehealth",
  },
  {
    icon: "ri-test-tube-line",
    title: "Metabolic Syndrome",
    description:
      "Comprehensive metabolic panel review, weight management, lipid disorders, and cardiovascular risk reduction strategies.",
    duration: "45 min",
    type: "In-Person / Telehealth",
  },
  {
    icon: "ri-video-line",
    title: "Telehealth Consultation",
    description:
      "Secure video consultations available nationwide. Same quality of care from the comfort of your home.",
    duration: "30–60 min",
    type: "Telehealth Only",
  },
  {
    icon: "ri-refresh-line",
    title: "Follow-Up Visit",
    description:
      "Ongoing management and monitoring for established patients. Lab review, medication adjustments, and care plan updates.",
    duration: "30 min",
    type: "In-Person / Telehealth",
  },
];

const insuranceList = [
  "Blue Cross Blue Shield",
  "Aetna",
  "Cigna",
  "UnitedHealthcare",
  "Humana",
  "Medicare",
  "Medicaid",
  "Tricare",
];

export default function ConsultationPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d786cc9rcamcd36dau40", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Medical Care</span>
          <h1
            className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Consultation Services
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto text-base leading-relaxed">
            Expert endocrinology care with a compassionate, faith-informed approach.
            In-person and telehealth appointments available.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2
            className="text-2xl md:text-3xl font-bold text-stone-800 mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Services Offered
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div
                key={s.title}
                className="p-6 rounded-xl border border-stone-100 bg-stone-50 hover:border-amber-200 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 mb-4 text-lg">
                  <i className={s.icon} />
                </div>
                <h3 className="font-bold text-stone-800 text-base mb-1">{s.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed mb-4">{s.description}</p>
                <div className="flex items-center gap-4 text-xs text-stone-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" /> {s.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line" /> {s.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Telehealth Info */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="flex-1">
              <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Virtual Care</span>
              <h2
                className="text-3xl font-bold text-stone-800 mt-2 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Telehealth Consultations
              </h2>
              <p className="text-stone-500 text-sm leading-relaxed mb-6">
                Dr. Sheldon Mckenzie offers secure, HIPAA-compliant video consultations for patients across the country.
                Receive the same expert endocrinology care from the comfort of your home.
              </p>
              <ul className="flex flex-col gap-3">
                {[
                  "Available in all 50 states",
                  "Secure, HIPAA-compliant video platform",
                  "Same-week appointments often available",
                  "Lab orders sent to your local lab",
                  "Prescriptions sent to your pharmacy",
                  "Follow-up care and ongoing management",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-stone-600">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 flex-shrink-0 text-xs">
                      <i className="ri-check-line" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 w-full lg:w-96 h-64 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/8e7f5975213815a35dd589ff7471468f.jpg"
                alt="Telehealth consultation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Coverage</span>
            <h2
              className="text-3xl font-bold text-stone-800 mt-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Insurance &amp; Fees
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-bold text-stone-800 mb-4 text-base">Accepted Insurance Plans</h3>
              <div className="grid grid-cols-2 gap-3">
                {insuranceList.map((ins) => (
                  <div
                    key={ins}
                    className="flex items-center gap-2 p-3 rounded-lg bg-stone-50 border border-stone-100 text-sm text-stone-600"
                  >
                    <i className="ri-check-line text-amber-700" />
                    {ins}
                  </div>
                ))}
              </div>
              <p className="text-xs text-stone-400 mt-4">
                * Please contact our office to verify your specific plan coverage before your appointment.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-stone-800 mb-4 text-base">Self-Pay Fees</h3>
              <div className="flex flex-col gap-3">
                {[
                  { service: "New Patient Consultation (60 min)", fee: "$350" },
                  { service: "Follow-Up Visit (30 min)", fee: "$175" },
                  { service: "Telehealth Consultation (45 min)", fee: "$250" },
                  { service: "Telehealth Follow-Up (30 min)", fee: "$150" },
                  { service: "Comprehensive Metabolic Review", fee: "$400" },
                ].map((item) => (
                  <div
                    key={item.service}
                    className="flex items-center justify-between p-4 rounded-lg bg-stone-50 border border-stone-100"
                  >
                    <span className="text-sm text-stone-600">{item.service}</span>
                    <span className="font-bold text-amber-700 text-sm">{item.fee}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-stone-400 mt-4">
                * Sliding scale fees available for qualifying patients. Ask about our financial assistance program.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-stone-50" id="book">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Appointments</span>
            <h2
              className="text-3xl font-bold text-stone-800 mt-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Request an Appointment
            </h2>
            <p className="text-stone-500 text-sm mt-2">
              Fill out the form below and our team will contact you within 24 hours to confirm your appointment.
            </p>
          </div>

          {formStatus === "success" ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-stone-100">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-2xl mx-auto mb-4">
                <i className="ri-check-line" />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Request Received!
              </h3>
              <p className="text-stone-500 text-sm">
                Thank you for reaching out. Our team will contact you within 24 hours to confirm your appointment.
              </p>
              <button
                onClick={() => setFormStatus("idle")}
                className="mt-6 px-6 py-2.5 rounded-full bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 transition-all cursor-pointer whitespace-nowrap"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="consultation-booking-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 border border-stone-100 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">First Name *</label>
                  <input
                    name="first_name"
                    type="text"
                    required
                    placeholder="John"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Last Name *</label>
                  <input
                    name="last_name"
                    type="text"
                    required
                    placeholder="Doe"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Phone Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    placeholder="(555) 000-0000"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Service Type *</label>
                  <select
                    name="service_type"
                    required
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400 bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="Diabetes Management">Diabetes Management</option>
                    <option value="Thyroid Disorders">Thyroid Disorders</option>
                    <option value="Hormonal Health">Hormonal Health</option>
                    <option value="Metabolic Syndrome">Metabolic Syndrome</option>
                    <option value="Telehealth Consultation">Telehealth Consultation</option>
                    <option value="Follow-Up Visit">Follow-Up Visit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Visit Type *</label>
                  <select
                    name="visit_type"
                    required
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400 bg-white"
                  >
                    <option value="">Select visit type</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Telehealth">Telehealth (Video)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Preferred Date</label>
                  <input
                    name="preferred_date"
                    type="date"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Insurance Provider</label>
                  <input
                    name="insurance_provider"
                    type="text"
                    placeholder="e.g. Blue Cross Blue Shield"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Reason for Visit / Additional Notes
                </label>
                <textarea
                  name="notes"
                  rows={4}
                  maxLength={500}
                  placeholder="Please briefly describe your symptoms or reason for the visit..."
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400 resize-none"
                />
                <p className="text-xs text-stone-400 mt-1">Max 500 characters</p>
              </div>
              {formStatus === "error" && (
                <p className="text-red-500 text-sm text-center">
                  Something went wrong. Please try again or call our office directly.
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 rounded-full bg-amber-700 text-white font-semibold text-sm hover:bg-amber-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Request Appointment"}
              </button>
              <p className="text-center text-xs text-stone-400">
                Our team will contact you within 24 hours to confirm your appointment.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
